import React from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const Divider = styled.div`
  background: #141413;
  height: 1px;
  width: 100%;
`;

const HeaderText = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #141413;
  margin: 0;
`;

const CardsWrapper = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const Card = styled.a`
  flex: 1;
  background: #ffffff;
  border: 1px solid #dddde2;
  border-radius: 8px;
  padding: 15px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-decoration: none;
  transition: box-shadow 0.2s ease;
  min-width: 0;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    text-decoration: none;
  }
`;

const CardContent = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  min-width: 0;
`;

const Thumbnail = styled.div`
  width: 90px;
  height: 127px;
  flex-shrink: 0;
  background: #ffffff;
  border: 1px solid #dddde2;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const Title = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: #141413;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
`;

const Author = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #535366;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledButton = styled(Button)`
  background: #0060de;
  border-color: #0060de;
  color: #ffffff;
  height: 40px;
  width: 100%;

  &:hover {
    background: #004bb8;
    border-color: #004bb8;
  }

  &:active {
    background: #003d99;
    border-color: #003d99;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
`;

interface PodcastData {
  id: number;
  workId: number;
  workTitle: string;
  workAuthors: string;
  workThumbnailUrl: string | null;
  source?: string;
}

interface Props {
  podcasts: PodcastData[];
}

/**
 * Podcast Library Ad 3 component that displays recommended podcasts.
 * Shows 2 podcast cards with thumbnails, titles, authors, and listen buttons.
 *
 * The listen button links to the podcast library with the podcast ID as a query
 * parameter to show the recommended/hoisted version.
 *
 * This component is automatically tracked with impression watching.
 */
export const PodcastLibraryAd3: React.FC<Props> = ({ podcasts }) => {
  const impressionSource = 'podcast_lib.home.ad-3';

  if (!podcasts || podcasts.length === 0) {
    return null;
  }

  const extra = {
    rec_type: podcasts[0]?.source,
  };

  return (
    <ImpressionWatcher active={true} source={impressionSource} extra={extra}>
      <Container>
        <Header>
          <Divider />
          <HeaderText>Podcasts you might like:</HeaderText>
        </Header>
        <CardsWrapper>
          {podcasts.map((podcast) => (
            <Card
              key={podcast.id}
              href={`${routes.podcast_library_url()}?recommendedPodcastId=${podcast.id}`}
            >
              <CardContent>
                <Thumbnail>
                  {podcast.workThumbnailUrl ? (
                    <ThumbnailImage src={podcast.workThumbnailUrl} alt="" />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        background: '#f8f8fb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon
                        name="article"
                        size={40}
                        style={{ color: '#dddde2' }}
                      />
                    </div>
                  )}
                </Thumbnail>
                <TextContent>
                  <Title>{podcast.workTitle}</Title>
                  <Author>By {podcast.workAuthors}</Author>
                </TextContent>
              </CardContent>
              <StyledButton>
                <IconWrapper>
                  <Icon name="headset" size={20} />
                </IconWrapper>
                Listen
              </StyledButton>
            </Card>
          ))}
        </CardsWrapper>
      </Container>
    </ImpressionWatcher>
  );
};
